<!--
  Job Log — ดูและสั่งงาน Hangfire ของ process ที่ SPA คุยด้วย (deployment `api`)
  แผนงาน: tasks/2026-09-18-hangfire-job-log/TASK.md

  ใช้แทน Hangfire dashboard `/mydashboard` ซึ่งเปิดได้เฉพาะ localhost (บน Azure ได้ 401)

  ⚠️ เข้าได้เฉพาะ Super admin (roleId 0) — ด่านจริงอยู่ที่ router.js (SuperAdminRights)
     และ APIAuthen.cs (listRole = ["0"]) · หน้านี้มีปุ่มสั่งงาน (Requeue / Delete / Trigger)
  ⚠️ ข้อมูลมาจาก Hangfire MemoryStorage ของ instance ที่ตอบ request นี้เท่านั้น —
     restart/deploy แล้วหายหมด และแต่ละ instance เห็นงานของตัวเอง (ต้องบอกบนหน้าจอเสมอ)
  ⚠️ ทุกข้อความผ่าน $t() คีย์ขึ้นต้น jobLog · ภาษาเริ่มต้นของระบบคือ 'en' — คีย์ต้องมีครบทั้ง th และ en
-->
<template>
  <v-container fluid>
    <v-skeleton-loader :loading="!flagNav" transition="scale-transition" max-height="64" type="table-heading">
      <v-toolbar :image="SubNavbarBackGround" color="primary" class="header-title text-white">
        <v-toolbar-title class="title" :style="'color: #' + FontColorTitle">
          {{ $t('_jobLog') }}
        </v-toolbar-title>
        <v-spacer />
        <div class="d-flex align-center pr-2" style="gap: 4px">
          <span class="text-caption mr-2 d-none d-sm-inline">{{ lastUpdatedText }}</span>
          <v-btn
            variant="text"
            color="white"
            size="small"
            :prepend-icon="autoRefresh ? 'mdi-pause' : 'mdi-play'"
            @click="autoRefresh = !autoRefresh"
          >
            {{ autoRefresh ? $t('jobLogAutoOn', { n: refreshSeconds }) : $t('jobLogAutoOff') }}
          </v-btn>
          <v-btn variant="text" color="white" size="small" prepend-icon="mdi-refresh" :loading="inFlight" @click="refresh()">
            {{ $t('jobLogRefresh') }}
          </v-btn>
        </div>
      </v-toolbar>
    </v-skeleton-loader>

    <!-- ══ อ่านครั้งแรกไม่ได้เลย — ไม่มีอะไรให้แสดง ══════════════════════════════════ -->
    <v-alert
      v-if="pageError && !overview"
      type="error"
      variant="tonal"
      class="mt-3 status-alert"
      :icon="pageError.icon"
    >
      <div class="text-body-2" style="font-weight: 500">{{ pageError.message }}</div>
      <v-btn
        class="mt-2"
        size="small"
        variant="tonal"
        color="error"
        prepend-icon="mdi-refresh"
        :loading="inFlight"
        @click="refresh()"
      >
        {{ $t('jobLogRetry') }}
      </v-btn>
    </v-alert>

    <!--
      อ่านรอบล่าสุดไม่สำเร็จ แต่ยังมีของเดิมค้างอยู่บนจอ — บอกว่าที่เห็นคือของเก่า
      ⚠️ ห้ามล้างจอทิ้ง · หน้านี้ poll ทุก 5 วินาที การกระพริบเป็นหน้าแดงทุกครั้งที่เน็ตสะดุด
         หนึ่งรอบ จะทำให้คนเลิกเชื่อหน้าจอนี้
    -->
    <v-alert
      v-if="staleError"
      type="warning"
      variant="tonal"
      density="compact"
      class="mt-3 status-alert"
      icon="mdi-clock-alert-outline"
    >
      <span class="text-body-2">{{ $t('jobLogStaleBanner', { message: staleError.message, when: lastUpdatedRelative }) }}</span>
    </v-alert>

    <!-- Hangfire ปิดบน deployment นี้ = ไม่มี worker มารันงาน · ปุ่มสั่งงานถูกปิดตาม -->
    <v-alert
      v-if="overview && !hangfireEnabled"
      type="warning"
      variant="tonal"
      class="mt-3 status-alert"
      icon="mdi-power-plug-off-outline"
    >
      <div class="text-body-2" style="font-weight: 500">{{ $t('jobLogHangfireOffTitle') }}</div>
      <div class="text-caption mt-1">{{ $t('jobLogHangfireOffBody') }}</div>
    </v-alert>

    <!-- ══ แถบข้อมูล instance ══════════════════════════════════════════════════════ -->
    <v-card v-if="overview || loadingOverview" class="qa-card mt-3" variant="flat">
      <v-progress-linear v-if="loadingOverview && !overview" indeterminate color="primary" />
      <v-card-text v-if="overview" class="py-3">
        <div class="d-flex align-center flex-wrap" style="gap: 8px">
          <v-chip size="small" variant="tonal" prepend-icon="mdi-server">
            {{ $t('jobLogInstance') }}:
            <span class="qa-num ml-1" style="font-weight: 500">{{ overview.instance || '—' }}</span>
            <v-tooltip activator="parent" location="top">
              {{ $t('jobLogMachine', { name: overview.machineName || '—' }) }}
            </v-tooltip>
          </v-chip>
          <v-chip size="small" variant="tonal" prepend-icon="mdi-restart">
            {{ $t('jobLogProcessStarted', { when: relative(overview.processStartedAtUtc) || '—' }) }}
            <v-tooltip activator="parent" location="top">{{ stamp(overview.processStartedAtUtc) }}</v-tooltip>
          </v-chip>
          <v-chip
            size="small"
            variant="flat"
            :color="hangfireEnabled ? 'success' : 'warning'"
            :prepend-icon="hangfireEnabled ? 'mdi-check-circle-outline' : 'mdi-alert-outline'"
          >
            {{ hangfireEnabled ? $t('jobLogHangfireOn') : $t('jobLogHangfireOff') }}
          </v-chip>
        </div>
        <ul class="note-list text-caption text-medium-emphasis mt-2">
          <li>{{ $t('jobLogNoteMemory') }}</li>
          <li>{{ $t('jobLogNoteMutualFund') }}</li>
          <li>{{ $t('jobLogNoteTimezone') }}</li>
        </ul>
      </v-card-text>
    </v-card>

    <!-- ══ การ์ดตัวเลข = ตัวเลือกแท็บ (ไม่มีแถบ v-tabs ซ้ำอีกชุด) ══════════════════════════ -->
    <v-row v-if="overview" dense class="mt-2">
      <v-col v-for="card in kpis" :key="card.key" cols="6" sm="4" md="3" lg>
        <div
          class="qa-card-sm pa-3 h-100 qa-clickable kpi-card"
          :class="{ 'kpi-card--active': activeTab === card.tab }"
          role="button"
          tabindex="0"
          :aria-pressed="activeTab === card.tab"
          @click="activeTab = card.tab"
          @keyup.enter="activeTab = card.tab"
        >
          <div class="d-flex align-center justify-space-between">
            <span class="text-caption text-medium-emphasis">{{ card.label }}</span>
            <v-icon size="18" :color="card.color">{{ card.icon }}</v-icon>
          </div>
          <div class="text-h5 qa-kpi-value mt-1" :class="card.valueClass" style="font-weight: 500">{{ card.value }}</div>
          <div v-if="card.sub" class="text-caption mt-1" :class="card.subClass || 'text-medium-emphasis'">{{ card.sub }}</div>
        </div>
      </v-col>
    </v-row>

    <!-- ══ แท็บรายการงาน ══════════════════════════════════════════════════════════ -->
    <v-card class="qa-card mt-3" variant="flat">
      <v-card-text class="py-3">
        <div class="d-flex align-center flex-wrap" style="gap: 12px">
          <v-text-field
            v-model="searchInput"
            :label="isRecurringTab ? $t('jobLogSearchRecurring') : $t('jobLogSearch')"
            prepend-inner-icon="mdi-magnify"
            density="compact"
            variant="outlined"
            clearable
            hide-details
            class="search-field"
          />
          <template v-if="!isRecurringTab">
            <v-switch v-model="hideEveryMinute" color="primary" density="compact" hide-details inset class="flex-grow-0">
              <template #label>
                <span class="text-body-2">{{ $t('jobLogHideEveryMinute') }}</span>
              </template>
            </v-switch>
            <span class="d-inline-flex">
              <v-icon size="18" color="info">mdi-information-outline</v-icon>
              <v-tooltip activator="parent" location="top" max-width="440">{{ everyMinuteHint }}</v-tooltip>
            </span>
          </template>
          <v-spacer />
          <span v-if="filteredHint" class="text-caption text-medium-emphasis">{{ filteredHint }}</span>
        </div>
      </v-card-text>

      <!--
        แท็บ state — ตาราง server-side (from/count) · :key = แท็บ ⇒ สลับแท็บแล้วตาราง mount ใหม่
        และยิง update:options เอง (โหลดหน้าแรกของแท็บใหม่) · ⚠️ อย่าเรียก loadJobs ซ้ำตอน mount
      -->
      <div v-if="!isRecurringTab" class="qa-table-wrap">
        <v-data-table-server
          :key="activeTab"
          v-model:items-per-page="pageSize"
          v-model:page="page"
          :headers="jobHeaders"
          :items="rows"
          :items-length="total"
          :loading="loadingJobs"
          :items-per-page-options="[10, 25, 50, 100]"
          item-value="jobId"
          class="elevation-0 my-data-table qa-table qa-table-wide"
          density="comfortable"
          fixed-header
          hover
          :row-props="{ class: 'qa-row-click' }"
          @click:row="onRowClick"
          @update:options="onTableOptions"
        >
          <template #[`item.name`]="{ item }">
            <div class="job-cell">
              <div class="d-flex align-center">
                <span class="text-body-2 text-truncate" style="font-weight: 500">{{ item.name }}</span>
                <v-chip v-if="isEveryMinuteJob(item)" size="x-small" variant="tonal" class="ml-2 flex-shrink-0">
                  {{ $t('jobLogEveryMinute') }}
                </v-chip>
              </div>
              <div class="text-caption text-medium-emphasis text-truncate">
                <span class="qa-num">#{{ item.jobId }}</span>
                <span v-if="item.method && item.method !== item.name"> · {{ item.method }}</span>
              </div>
            </div>
          </template>

          <template #[`item.args`]="{ item }">
            <span class="mono text-caption args-cell">{{ truncate(argsText(item.args), 60) }}</span>
          </template>

          <template #[`item.stateAtUtc`]="{ item }">
            <div class="text-body-2 text-no-wrap qa-num">{{ clock(item.stateAtUtc) }}</div>
            <div class="text-caption text-medium-emphasis text-no-wrap">
              {{ day(item.stateAtUtc) }}<span v-if="relative(item.stateAtUtc)"> · {{ relative(item.stateAtUtc) }}</span>
            </div>
          </template>

          <template #[`item.elapsed`]="{ item }">
            <span class="text-body-2 qa-num text-no-wrap" style="font-weight: 500">{{ elapsedSince(item.stateAtUtc) }}</span>
          </template>

          <template #[`item.serverId`]="{ item }">
            <span class="mono text-caption server-cell">
              {{ item.serverId || '—' }}
              <v-tooltip v-if="item.serverId" activator="parent" location="top">{{ item.serverId }}</v-tooltip>
            </span>
          </template>

          <template #[`item.scheduledAtUtc`]="{ item }">
            <div class="text-body-2 text-no-wrap qa-num">{{ clock(item.scheduledAtUtc) }}</div>
            <div class="text-caption text-medium-emphasis text-no-wrap">{{ day(item.scheduledAtUtc) }}</div>
          </template>

          <template #[`item.durationMs`]="{ item }">
            <span class="text-body-2 qa-num text-no-wrap">{{ fmtDuration(item.durationMs) }}</span>
          </template>

          <template #[`item.result`]="{ item }">
            <span class="mono text-caption clamp-2">{{ item.result || '—' }}</span>
          </template>

          <template #[`item.exception`]="{ item }">
            <div class="exc-cell">
              <div class="text-caption text-error" style="font-weight: 500">{{ shortType(item.exceptionType) || '—' }}</div>
              <div v-if="isLockTimeout(item.exceptionType)" class="text-caption text-warning d-flex align-center">
                <v-icon size="14" color="warning" class="mr-1">mdi-lock-clock</v-icon>
                {{ $t('jobLogLockTimeout') }}
              </div>
              <div class="text-caption text-medium-emphasis clamp-2">{{ item.exceptionMessage || item.reason || '' }}</div>
            </div>
          </template>

          <template #[`item.actions`]="{ item }">
            <div class="action-buttons-group">
              <v-btn
                v-if="canRequeue(item.state)"
                icon
                size="small"
                variant="text"
                color="primary"
                class="action-btn"
                :disabled="!actionsEnabled"
                @click.stop="askRequeue(item)"
              >
                <v-icon size="18">{{ item.state === 'Scheduled' ? 'mdi-play-speed' : 'mdi-replay' }}</v-icon>
                <v-tooltip activator="parent" location="top">{{ requeueLabel(item.state) }}</v-tooltip>
              </v-btn>
              <v-btn
                v-if="canDelete(item.state)"
                icon
                size="small"
                variant="text"
                color="error"
                class="action-btn"
                :disabled="!actionsEnabled"
                @click.stop="askDelete(item)"
              >
                <v-icon size="18">mdi-delete-outline</v-icon>
                <v-tooltip activator="parent" location="top">{{ $t('jobLogActionDelete') }}</v-tooltip>
              </v-btn>
            </div>
          </template>

          <template #no-data>
            <div class="text-center py-8">
              <template v-if="jobsError">
                <v-icon size="40" color="error">{{ jobsError.icon }}</v-icon>
                <div class="text-body-2 mt-2">{{ jobsError.message }}</div>
              </template>
              <template v-else>
                <v-icon size="40" class="text-medium-emphasis">mdi-tray-remove</v-icon>
                <div class="text-subtitle-1 mt-2">{{ $t('jobLogEmpty') }}</div>
                <div v-if="hideEveryMinute || search" class="text-caption text-medium-emphasis">
                  {{ $t('jobLogEmptyFiltered') }}
                </div>
              </template>
            </div>
          </template>
        </v-data-table-server>
      </div>

      <!-- แท็บ Recurring — ข้อมูลมากับ overview (มีไม่กี่ตัว) กรองด้วยช่องค้นหาฝั่งหน้าจอ -->
      <div v-else class="qa-table-wrap">
        <v-data-table
          :headers="recurringHeaders"
          :items="recurringRows"
          :loading="loadingOverview && !overview"
          item-value="id"
          :items-per-page="-1"
          hide-default-footer
          class="elevation-0 my-data-table qa-table qa-table-wide"
          density="comfortable"
          fixed-header
          hover
        >
          <template #[`item.id`]="{ item }">
            <div class="job-cell">
              <div class="d-flex align-center">
                <span class="text-body-2 text-truncate" style="font-weight: 500">{{ item.id }}</span>
                <v-chip v-if="item.isEveryMinute" size="x-small" variant="tonal" class="ml-2 flex-shrink-0">
                  {{ $t('jobLogEveryMinute') }}
                </v-chip>
              </div>
              <div class="text-caption text-medium-emphasis text-truncate">{{ item.jobName || '—' }}</div>
            </div>
          </template>

          <template #[`item.cron`]="{ item }">
            <code class="mono text-body-2">{{ item.cron || '—' }}</code>
            <div class="text-caption text-medium-emphasis">{{ item.timeZoneId || '' }}</div>
          </template>

          <template #[`item.nextExecutionUtc`]="{ item }">
            <div class="text-body-2 text-no-wrap qa-num">{{ clock(item.nextExecutionUtc) }}</div>
            <div class="text-caption text-medium-emphasis text-no-wrap">
              {{ day(item.nextExecutionUtc) }}<span v-if="relative(item.nextExecutionUtc)"> · {{ relative(item.nextExecutionUtc) }}</span>
            </div>
          </template>

          <template #[`item.lastExecutionUtc`]="{ item }">
            <div class="d-flex align-center flex-wrap" style="gap: 6px">
              <span class="text-body-2 text-no-wrap qa-num">{{ clock(item.lastExecutionUtc) }}</span>
              <v-chip
                v-if="item.lastJobState"
                size="x-small"
                variant="tonal"
                :color="stateColor(item.lastJobState)"
                :class="item.lastJobId ? 'qa-clickable' : ''"
                @click.stop="item.lastJobId && openDetail(item.lastJobId)"
              >
                {{ stateLabel(item.lastJobState) }}
                <v-tooltip v-if="item.lastJobId" activator="parent" location="top">
                  {{ $t('jobLogOpenLastJob', { id: item.lastJobId }) }}
                </v-tooltip>
              </v-chip>
            </div>
            <div class="text-caption text-medium-emphasis text-no-wrap">
              {{ day(item.lastExecutionUtc) }}<span v-if="relative(item.lastExecutionUtc)"> · {{ relative(item.lastExecutionUtc) }}</span>
            </div>
          </template>

          <template #[`item.error`]="{ item }">
            <span v-if="item.error" class="text-caption text-error clamp-2">{{ item.error }}</span>
            <span v-else class="text-medium-emphasis">—</span>
          </template>

          <template #[`item.actions`]="{ item }">
            <div class="action-buttons-group">
              <v-btn
                icon
                size="small"
                variant="text"
                color="primary"
                class="action-btn"
                :disabled="!actionsEnabled"
                @click.stop="askTrigger(item)"
              >
                <v-icon size="18">mdi-play-circle-outline</v-icon>
                <v-tooltip activator="parent" location="top">{{ $t('jobLogActionTrigger') }}</v-tooltip>
              </v-btn>
            </div>
          </template>

          <template #no-data>
            <div class="text-center py-8">
              <v-icon size="40" class="text-medium-emphasis">mdi-calendar-remove-outline</v-icon>
              <div class="text-subtitle-1 mt-2">{{ $t('jobLogRecurringEmpty') }}</div>
            </div>
          </template>
        </v-data-table>
      </div>
    </v-card>

    <!-- ══ Server / worker ════════════════════════════════════════════════════════ -->
    <v-card v-if="overview" class="qa-card mt-3" variant="flat">
      <v-card-title class="d-flex align-center flex-wrap py-3" style="gap: 8px">
        <v-icon size="20" color="primary">mdi-server-network</v-icon>
        <span class="text-subtitle-1" style="font-weight: 500">{{ $t('jobLogServersTitle') }}</span>
        <v-spacer />
        <span class="text-caption text-medium-emphasis">{{ queueSummary }}</span>
      </v-card-title>
      <v-divider />
      <div class="qa-table-wrap pt-3">
        <v-table density="compact" class="qa-table servers-table">
          <thead>
            <tr>
              <th>{{ $t('jobLogServerName') }}</th>
              <th class="text-end">{{ $t('jobLogServerWorkers') }}</th>
              <th>{{ $t('jobLogServerQueues') }}</th>
              <th>{{ $t('jobLogServerStarted') }}</th>
              <th>{{ $t('jobLogServerHeartbeat') }}</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="server in servers" :key="server.name">
              <td>
                <span class="mono text-caption server-cell">
                  {{ server.name }}
                  <v-tooltip activator="parent" location="top">{{ server.name }}</v-tooltip>
                </span>
              </td>
              <td class="text-end qa-num">{{ num(server.workersCount) }}</td>
              <td class="text-caption">{{ (server.queues || []).join(', ') || '—' }}</td>
              <td class="text-caption text-no-wrap">{{ stamp(server.startedAtUtc) }}</td>
              <td class="text-caption text-no-wrap" :class="isHeartbeatStale(server.heartbeatUtc) ? 'text-warning' : ''">
                {{ relative(server.heartbeatUtc) || stamp(server.heartbeatUtc) }}
              </td>
            </tr>
            <tr v-if="!servers.length">
              <td colspan="5" class="text-center text-medium-emphasis py-4">
                {{ hangfireEnabled ? $t('jobLogNoServers') : $t('jobLogNoServersDisabled') }}
              </td>
            </tr>
          </tbody>
        </v-table>
      </div>
    </v-card>

    <!-- ══ รายละเอียดงาน ═══════════════════════════════════════════════════════════ -->
    <v-dialog v-model="detailOpen" max-width="1000" scrollable>
      <v-card>
        <v-card-title class="bg-primary text-white d-flex align-center" style="gap: 8px">
          <v-icon>mdi-briefcase-search-outline</v-icon>
          <span class="text-truncate">{{ detail ? detail.name : $t('jobLogDetailTitle') }}</span>
          <v-chip v-if="detail" size="small" variant="flat" :color="stateColor(detail.state)" class="flex-shrink-0">
            {{ stateLabel(detail.state) }}
          </v-chip>
          <v-spacer />
          <v-btn icon="mdi-close" size="small" variant="text" color="white" @click="detailOpen = false" />
        </v-card-title>

        <v-card-text class="pt-4">
          <v-progress-linear v-if="detailLoading" indeterminate color="primary" class="mb-3" />
          <v-alert v-if="detailError" type="error" variant="tonal" density="compact" class="mb-3" :icon="detailError.icon">
            {{ detailError.message }}
          </v-alert>

          <template v-if="detail">
            <v-row dense>
              <v-col v-for="field in detailFields" :key="field.label" cols="12" sm="6" md="4">
                <div class="text-caption text-medium-emphasis">{{ field.label }}</div>
                <div class="text-body-2 text-break" :class="field.mono ? 'mono' : ''">{{ field.value }}</div>
              </v-col>
            </v-row>

            <!-- args -->
            <div class="section-title mt-4">{{ $t('jobLogColArgs') }}</div>
            <template v-if="detail.args && detail.args.length">
              <pre v-for="(arg, i) in detail.args" :key="i" class="code-block mb-1">{{ arg }}</pre>
            </template>
            <div v-else class="text-caption text-medium-emphasis">{{ $t('jobLogNoArgs') }}</div>

            <!-- ผลลัพธ์ (Succeeded) -->
            <template v-if="detail.result">
              <div class="section-title mt-4">{{ $t('jobLogColResult') }}</div>
              <pre class="code-block">{{ detail.result }}</pre>
            </template>

            <!-- exception (Failed ล่าสุด) -->
            <template v-if="detail.exceptionType || detail.exceptionMessage || detail.exceptionDetails">
              <div class="section-title mt-4">{{ $t('jobLogColException') }}</div>
              <v-alert
                v-if="isLockTimeout(detail.exceptionType)"
                type="warning"
                variant="tonal"
                density="compact"
                class="mb-2"
                icon="mdi-lock-clock"
              >
                <div class="text-body-2" style="font-weight: 500">{{ $t('jobLogLockTimeout') }}</div>
                <div class="text-caption">{{ $t('jobLogLockTimeoutHint') }}</div>
              </v-alert>
              <div class="text-body-2 text-error mono text-break" style="font-weight: 500">{{ detail.exceptionType || '—' }}</div>
              <div v-if="detail.exceptionMessage" class="text-body-2 mt-1 text-break">{{ detail.exceptionMessage }}</div>
              <pre v-if="detail.exceptionDetails" class="code-block code-block--tall mt-2">{{ detail.exceptionDetails }}</pre>
            </template>

            <!-- parameters -->
            <div class="section-title mt-4">{{ $t('jobLogParameters') }}</div>
            <table v-if="detailParams.length" class="kv-table">
              <tbody>
                <tr v-for="param in detailParams" :key="param.key">
                  <td class="kv-key mono">{{ param.key }}</td>
                  <td class="mono text-break">{{ param.value }}</td>
                </tr>
              </tbody>
            </table>
            <div v-else class="text-caption text-medium-emphasis">—</div>

            <!-- ประวัติ state ใหม่สุดก่อน -->
            <div class="section-title mt-4">{{ $t('jobLogHistory') }}</div>
            <div v-if="!detailHistory.length" class="text-caption text-medium-emphasis">—</div>
            <div v-for="(entry, i) in detailHistory" :key="i" class="history-item">
              <div class="d-flex align-center flex-wrap" style="gap: 8px">
                <v-chip size="small" variant="tonal" :color="stateColor(entry.state)" :prepend-icon="stateIcon(entry.state)">
                  {{ stateLabel(entry.state) }}
                </v-chip>
                <span class="text-body-2 qa-num">{{ stamp(entry.createdAtUtc) }}</span>
                <span class="text-caption text-medium-emphasis">{{ relative(entry.createdAtUtc) }}</span>
              </div>
              <div v-if="entry.reason" class="text-caption mt-1">{{ entry.reason }}</div>
              <table v-if="dataEntries(entry).length" class="kv-table mt-1">
                <tbody>
                  <tr v-for="row in dataEntries(entry)" :key="row.key">
                    <td class="kv-key mono">{{ row.key }}</td>
                    <td>
                      <pre v-if="row.long" class="code-block">{{ row.value }}</pre>
                      <span v-else class="mono text-break">{{ row.value }}</span>
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          </template>
        </v-card-text>

        <v-divider />
        <v-card-actions class="pa-4">
          <v-spacer />
          <v-btn variant="outlined" prepend-icon="mdi-close" @click="detailOpen = false">{{ $t('jobLogClose') }}</v-btn>
          <v-btn
            v-if="detail && canDelete(detail.state)"
            color="error"
            variant="elevated"
            prepend-icon="mdi-delete-outline"
            :disabled="!actionsEnabled"
            @click="askDelete(detail)"
          >
            {{ $t('jobLogActionDelete') }}
          </v-btn>
          <v-btn
            v-if="detail && canRequeue(detail.state)"
            color="primary"
            variant="elevated"
            :prepend-icon="detail.state === 'Scheduled' ? 'mdi-play-speed' : 'mdi-replay'"
            :disabled="!actionsEnabled"
            @click="askRequeue(detail)"
          >
            {{ requeueLabel(detail.state) }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- ══ ยืนยันการสั่งงาน ═══════════════════════════════════════════════════════ -->
    <v-dialog v-model="confirmOpen" max-width="520" :persistent="actionBusy">
      <v-card>
        <v-card-title :class="`bg-${confirmView.color} text-white d-flex align-center`" style="gap: 8px">
          <v-icon>{{ confirmView.icon }}</v-icon>
          <span>{{ confirmView.title }}</span>
        </v-card-title>
        <v-card-text class="pt-4">
          <p class="text-body-2">{{ confirmView.body }}</p>
          <v-alert v-if="confirmView.note" type="info" variant="tonal" density="compact" class="mt-3">
            <span class="text-caption">{{ confirmView.note }}</span>
          </v-alert>
        </v-card-text>
        <v-card-actions class="pa-4">
          <v-spacer />
          <v-btn variant="outlined" prepend-icon="mdi-close" :disabled="actionBusy" @click="confirmOpen = false">
            {{ $t('jobLogCancel') }}
          </v-btn>
          <v-btn
            :color="confirmView.color"
            variant="elevated"
            :prepend-icon="confirmView.buttonIcon"
            :loading="actionBusy"
            @click="runConfirmed"
          >
            {{ confirmView.button }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <v-snackbar v-model="snackbar.show" :color="snackbar.color" :timeout="3000">
      {{ snackbar.text }}
    </v-snackbar>
  </v-container>
</template>

<script>
/**
 * Job Log — หน้าดูและสั่งงาน Hangfire (Processing / Enqueued / Scheduled / Succeeded / Failed / Deleted / Recurring)
 *
 * ⚠️ ชั้น API คือ services/jobLogApi.js — **ห้ามใช้ $call** เพราะหน้านี้ poll ทุก 5 วินาที
 *    และ apiGetAuth เด้ง Swal ทุกครั้งที่ error (API ล่มหนึ่งนาที = Swal ซ้อนกัน 12 อัน)
 * ⚠️ เวลาทุกตัวจาก API เป็น UTC — parse ผ่าน utils/thaiTime.js (ปักที่ Asia/Bangkok) เท่านั้น
 * ⚠️ งานที่กำลังรันหยุดจากหน้านี้ไม่ได้ (โค้ดใน job ไม่รับ cancellation token) — ไม่มีปุ่มโดยตั้งใจ
 */
import jobLogApi, { API_STATUS_NOT_FOUND } from '@/services/jobLogApi';
import { parseUtcDate, fmtThaiDateTime, fmtThaiTime } from '@/utils/thaiTime';
// สไตล์ร่วม (.qa-card, .qa-table) — ยึดตาม docs/Table-Style-Guide.md
import '@/assets/qa-console.css';

/** รอบการถามซ้ำ */
const REFRESH_MS = 5000;

/** นาฬิกาของหน้าจอ — ให้ "รันมาแล้ว 12 วินาที" เดินจริง ไม่ค้างรอ poll รอบหน้า */
const CLOCK_MS = 1000;

const SEARCH_DEBOUNCE_MS = 400;

/** heartbeat ของ Hangfire server เต้นทุก ~30 วินาที · เงียบเกิน 2 นาที = น่าสงสัย */
const HEARTBEAT_STALE_MS = 2 * 60 * 1000;

const RECURRING_TAB = 'Recurring';

/**
 * state ของ Hangfire → คีย์ป้าย / สี / ไอคอน / คีย์หัวคอลัมน์เวลา
 * ⚠️ สีผ่าน prop ของ Vuetify เท่านั้น · Deleted ไม่ใส่สี (ชิปกลาง ๆ) ให้ต่างจาก Scheduled
 */
const STATE_META = {
  Processing: { labelKey: 'jobLogStateProcessing', color: 'primary', icon: 'mdi-cog-play-outline', countKey: 'processing', timeKey: 'jobLogColStartedAt' },
  Enqueued: { labelKey: 'jobLogStateEnqueued', color: 'info', icon: 'mdi-tray-full', countKey: 'enqueued', timeKey: 'jobLogColEnqueuedAt' },
  Scheduled: { labelKey: 'jobLogStateScheduled', color: 'secondary', icon: 'mdi-calendar-clock-outline', countKey: 'scheduled', timeKey: 'jobLogColEnqueueAt' },
  Succeeded: { labelKey: 'jobLogStateSucceeded', color: 'success', icon: 'mdi-check-circle-outline', countKey: 'succeeded', timeKey: 'jobLogColSucceededAt' },
  Failed: { labelKey: 'jobLogStateFailed', color: 'error', icon: 'mdi-alert-circle-outline', countKey: 'failed', timeKey: 'jobLogColFailedAt' },
  Deleted: { labelKey: 'jobLogStateDeleted', color: undefined, icon: 'mdi-delete-outline', countKey: 'deleted', timeKey: 'jobLogColDeletedAt' },
  Awaiting: { labelKey: 'jobLogStateAwaiting', color: 'secondary', icon: 'mdi-timer-sand', countKey: 'awaiting', timeKey: '' },
};

/** ลำดับแท็บ state (Recurring ต่อท้ายแยก) */

/** กติกาเดียวกับ server (TASK.md contract ข้อ 4-5) — ซ่อนปุ่มที่ server จะปฏิเสธอยู่แล้ว */
const REQUEUE_STATES = ['Failed', 'Succeeded', 'Deleted', 'Scheduled'];
const DELETE_STATES = ['Enqueued', 'Scheduled', 'Failed'];

/**
 * lock timeout ของ [DisableConcurrentExecution] — บน MemoryStorage โยน SynchronizationLockException
 * ("Object synchronization method was called from an unsynchronized block of code.") ซึ่งอ่านไม่ออกเลย
 * ว่าคือ "รอบก่อนยังรันอยู่" · DistributedLockTimeoutException คือตัวเดียวกันบน storage แบบอื่น
 */
const LOCK_TIMEOUT_TYPES = ['SynchronizationLockException', 'DistributedLockTimeoutException'];

const DONE_KEYS = {
  requeue: 'jobLogDoneRequeue',
  delete: 'jobLogDoneDelete',
  trigger: 'jobLogDoneTrigger',
};

export default {
  name: 'JobLog',
  // toolbar ธีมเดียวกับหน้าอื่นในระบบ — ค่ามาจาก provide ของ App.vue
  inject: ['flagNav', 'FontColorTitle', 'SubNavbarBackGround'],

  data() {
    return {
      // ── overview ──
      overview: null,
      loadingOverview: true,
      /** อ่านครั้งแรกไม่ได้ (ยังไม่มีอะไรให้แสดง) */
      pageError: null,
      /** อ่านรอบหลังไม่ได้แต่มีของเดิม — ขึ้นป้ายว่าเป็นของเก่า */
      overviewStale: null,
      lastUpdatedAt: null,

      // ── แท็บ + ตาราง ──
      activeTab: 'Processing',
      rows: [],
      total: 0,
      page: 1,
      pageSize: 25,
      loadingJobs: false,
      jobsError: null,
      jobsStale: null,
      /** เลขลำดับคำขอ — ทิ้งผลของคำขอเก่าที่กลับมาทีหลัง (สลับแท็บ/เปลี่ยนหน้าเร็ว ๆ) */
      jobsSeq: 0,

      // ── ตัวกรอง ──
      searchInput: '',
      search: '',
      searchTimer: null,
      /** ค่าเริ่มต้นเปิด — ไม่งั้นแท็บสำเร็จเต็มไปด้วย dispatcher รายนาที ~4,300 แถว/วัน */
      hideEveryMinute: true,

      // ── polling ──
      autoRefresh: true,
      inFlight: false,
      poll: null,
      // ⚠️ ห้ามตั้งชื่อ `clock` — ชนกับเมธอด clock(value) แล้ว data ทับเมธอด ⇒ render พังทั้งหน้า
      clockTimer: null,
      now: Date.now(),
      refreshSeconds: Math.round(REFRESH_MS / 1000),

      // ── รายละเอียด ──
      detailOpen: false,
      detailLoading: false,
      detail: null,
      detailError: null,
      detailSeq: 0,

      // ── ยืนยันการสั่งงาน ──
      confirmOpen: false,
      confirm: null,
      actionBusy: false,

      snackbar: { show: false, text: '', color: 'success' },
    };
  },

  computed: {
    /** สถานะที่แสดง — ยังไม่รู้ (ยังไม่โหลด) ถือว่าเปิด เพื่อไม่ให้แบนเนอร์ "ปิด" กระพริบตอนเปิดหน้า */
    hangfireEnabled() {
      return !this.overview || this.overview.hangfireEnabled !== false;
    },
    /** ปุ่มสั่งงานกดได้ก็ต่อเมื่อ **รู้แน่** ว่า Hangfire เปิดอยู่ */
    actionsEnabled() {
      return Boolean(this.overview) && this.overview.hangfireEnabled !== false;
    },
    counts() {
      return (this.overview && this.overview.counts) || {};
    },
    servers() {
      return (this.overview && this.overview.servers) || [];
    },
    queues() {
      return (this.overview && this.overview.queues) || [];
    },
    recurring() {
      return (this.overview && this.overview.recurring) || [];
    },
    everyMinuteIds() {
      return this.recurring.filter((r) => r.isEveryMinute).map((r) => r.id);
    },
    everyMinuteHint() {
      return this.everyMinuteIds.length
        ? this.$t('jobLogHideEveryMinuteHint', { list: this.everyMinuteIds.join(', ') })
        : this.$t('jobLogHideEveryMinuteHintNone');
    },
    /** worker เต็ม = processing ≥ จำนวน worker · งานใหม่ต้องรอคิวจนกว่าจะมีตัวว่าง */
    workersFull() {
      const workers = Number(this.counts.workers) || 0;
      return workers > 0 && (Number(this.counts.processing) || 0) >= workers;
    },
    workersSub() {
      const workers = Number(this.counts.workers) || 0;
      if (workers <= 0) return this.hangfireEnabled ? this.$t('jobLogNoWorkers') : this.$t('jobLogHangfireOff');
      const text = this.$t('jobLogWorkersUsed', { used: this.num(this.counts.processing), total: this.num(workers) });
      return this.workersFull ? `${text} — ${this.$t('jobLogWorkersFull')}` : text;
    },
    kpis() {
      const c = this.counts;
      const awaiting = Number(c.awaiting) || 0;
      const failed = Number(c.failed) || 0;
      return [
        {
          key: 'processing',
          tab: 'Processing',
          label: this.$t('jobLogStateProcessing'),
          value: this.num(c.processing),
          icon: STATE_META.Processing.icon,
          color: this.workersFull ? 'warning' : STATE_META.Processing.color,
          valueClass: this.workersFull ? 'text-warning' : '',
          sub: this.workersSub,
          subClass: this.workersFull ? 'text-warning' : '',
        },
        {
          key: 'enqueued',
          tab: 'Enqueued',
          label: this.$t('jobLogStateEnqueued'),
          value: this.num(c.enqueued),
          icon: STATE_META.Enqueued.icon,
          color: STATE_META.Enqueued.color,
          sub: awaiting > 0 ? this.$t('jobLogAwaitingSub', { n: this.num(awaiting) }) : '',
        },
        {
          key: 'scheduled',
          tab: 'Scheduled',
          label: this.$t('jobLogStateScheduled'),
          value: this.num(c.scheduled),
          icon: STATE_META.Scheduled.icon,
          color: STATE_META.Scheduled.color,
        },
        {
          key: 'succeeded',
          tab: 'Succeeded',
          label: this.$t('jobLogStateSucceeded'),
          value: this.num(c.succeeded),
          icon: STATE_META.Succeeded.icon,
          color: STATE_META.Succeeded.color,
          sub: this.$t('jobLogSucceededSinceStart', { n: this.num(c.succeededSinceStart) }),
        },
        {
          key: 'failed',
          tab: 'Failed',
          label: this.$t('jobLogStateFailed'),
          value: this.num(failed),
          icon: STATE_META.Failed.icon,
          color: STATE_META.Failed.color,
          valueClass: failed > 0 ? 'text-error' : '',
        },
        {
          key: 'deleted',
          tab: 'Deleted',
          label: this.$t('jobLogStateDeleted'),
          value: this.num(c.deleted),
          icon: STATE_META.Deleted.icon,
          color: STATE_META.Deleted.color,
        },
        {
          key: 'recurring',
          tab: RECURRING_TAB,
          label: this.$t('jobLogTabRecurring'),
          value: this.num(c.recurring ?? this.recurring.length),
          icon: 'mdi-calendar-sync-outline',
          color: 'primary',
          sub: this.everyMinuteIds.length ? this.$t('jobLogRecurringEveryMinuteSub', { n: this.everyMinuteIds.length }) : '',
        },
      ];
    },
    isRecurringTab() {
      return this.activeTab === RECURRING_TAB;
    },
    /**
     * ตัวเลขบนแท็บนับทุกงาน (ไม่ขึ้นกับตัวกรอง) ส่วนตารางแสดงหลังกรอง — ต้องบอกส่วนต่าง
     * ไม่งั้นแท็บสำเร็จบอก 4,300 แต่ตารางมี 12 แถว แล้วคนสรุปว่าข้อมูลหาย
     */
    filteredHint() {
      if (!this.overview) return '';
      if (this.isRecurringTab) {
        if (!this.search) return '';
        return this.$t('jobLogFilteredHint', { shown: this.num(this.recurringRows.length), all: this.num(this.recurring.length) });
      }
      if (!this.hideEveryMinute && !this.search) return '';
      const meta = STATE_META[this.activeTab];
      const all = Number(meta && this.counts[meta.countKey]) || 0;
      if (this.total >= all) return '';
      return this.$t('jobLogFilteredHint', { shown: this.num(this.total), all: this.num(all) });
    },
    jobHeaders() {
      const state = this.activeTab;
      const meta = STATE_META[state] || {};
      const headers = [
        { title: this.$t('jobLogColJob'), key: 'name', sortable: false, minWidth: '260px' },
        { title: this.$t('jobLogColArgs'), key: 'args', sortable: false, minWidth: '160px' },
        { title: meta.timeKey ? this.$t(meta.timeKey) : '', key: 'stateAtUtc', sortable: false, width: '180px' },
      ];
      if (state === 'Processing') {
        headers.push(
          { title: this.$t('jobLogColElapsed'), key: 'elapsed', sortable: false, width: '140px' },
          { title: this.$t('jobLogColServer'), key: 'serverId', sortable: false, minWidth: '180px' },
        );
      }
      if (state === 'Scheduled') {
        headers.push({ title: this.$t('jobLogColScheduledAt'), key: 'scheduledAtUtc', sortable: false, width: '160px' });
      }
      if (state === 'Succeeded') {
        headers.push(
          { title: this.$t('jobLogColDuration'), key: 'durationMs', sortable: false, align: 'end', width: '130px' },
          { title: this.$t('jobLogColResult'), key: 'result', sortable: false, minWidth: '220px' },
        );
      }
      if (state === 'Failed') {
        headers.push({ title: this.$t('jobLogColException'), key: 'exception', sortable: false, minWidth: '320px' });
      }
      if (this.canRequeue(state) || this.canDelete(state)) {
        headers.push({ title: '', key: 'actions', sortable: false, align: 'end', width: '100px', fixed: 'end' });
      }
      return headers;
    },
    recurringHeaders() {
      return [
        { title: this.$t('jobLogColRecurringId'), key: 'id', sortable: false, minWidth: '280px' },
        { title: this.$t('jobLogColCron'), key: 'cron', sortable: false, width: '150px' },
        { title: this.$t('jobLogColNextRun'), key: 'nextExecutionUtc', sortable: false, width: '190px' },
        { title: this.$t('jobLogColLastRun'), key: 'lastExecutionUtc', sortable: false, minWidth: '220px' },
        { title: this.$t('jobLogColError'), key: 'error', sortable: false, minWidth: '220px' },
        { title: '', key: 'actions', sortable: false, align: 'end', width: '72px', fixed: 'end' },
      ];
    },
    recurringRows() {
      const q = this.search.toLowerCase();
      if (!q) return this.recurring;
      return this.recurring.filter((r) =>
        [r.id, r.jobName, r.cron, r.lastJobId, r.lastJobState, r.error]
          .filter(Boolean)
          .some((v) => String(v).toLowerCase().includes(q)),
      );
    },
    queueSummary() {
      if (!this.queues.length) return '';
      return this.queues
        .map((q) => this.$t('jobLogQueueSummary', { name: q.name, length: this.num(q.length), fetched: this.num(q.fetched) }))
        .join(' · ');
    },
    staleError() {
      return this.overviewStale || this.jobsStale;
    },
    lastUpdatedRelative() {
      if (!this.lastUpdatedAt) return this.$t('jobLogNeverLoaded');
      return this.relative(new Date(this.lastUpdatedAt)) || this.$t('jobLogJustNow');
    },
    lastUpdatedText() {
      if (!this.lastUpdatedAt) return this.$t('jobLogNeverLoaded');
      return this.$t('jobLogUpdatedAt', { when: fmtThaiTime(new Date(this.lastUpdatedAt)) });
    },

    // ── รายละเอียด ──
    detailFields() {
      const d = this.detail;
      if (!d) return [];
      const meta = STATE_META[d.state] || {};
      const fields = [
        { label: this.$t('jobLogFieldJobId'), value: d.jobId || '—', mono: true },
        { label: this.$t('jobLogFieldMethod'), value: d.method || '—', mono: true },
        { label: this.$t('jobLogFieldRecurring'), value: d.recurringJobId || '—', mono: true },
        { label: this.$t('jobLogFieldCreated'), value: this.stampRelative(d.createdAtUtc) },
        { label: meta.timeKey ? this.$t(meta.timeKey) : this.$t('jobLogFieldStateAt'), value: this.stampRelative(d.stateAtUtc) },
        { label: this.$t('jobLogFieldExpire'), value: d.expireAtUtc ? this.stampRelative(d.expireAtUtc) : this.$t('jobLogNoExpire') },
      ];
      if (d.scheduledAtUtc) fields.push({ label: this.$t('jobLogColScheduledAt'), value: this.stampRelative(d.scheduledAtUtc) });
      if (d.serverId) fields.push({ label: this.$t('jobLogColServer'), value: d.serverId, mono: true });
      if (d.state === 'Processing') fields.push({ label: this.$t('jobLogColElapsed'), value: this.elapsedSince(d.stateAtUtc) });
      if (d.durationMs !== null && d.durationMs !== undefined) {
        fields.push({ label: this.$t('jobLogColDuration'), value: this.fmtDuration(d.durationMs) });
      }
      if (d.reason) fields.push({ label: this.$t('jobLogFieldReason'), value: d.reason });
      return fields;
    },
    /** parameters ถูก unquote มาจาก server แล้ว */
    detailParams() {
      const params = (this.detail && this.detail.parameters) || {};
      return Object.keys(params).map((key) => ({ key, value: params[key] ?? '—' }));
    },
    /** server เรียงใหม่สุดก่อนมาให้แล้ว — ไม่ sort ซ้ำ */
    detailHistory() {
      return (this.detail && this.detail.history) || [];
    },

    // ── ยืนยัน ──
    confirmView() {
      const c = this.confirm;
      if (!c) return { color: 'primary', icon: '', title: '', body: '', note: '', button: '', buttonIcon: '' };
      if (c.kind === 'delete') {
        return {
          color: 'error',
          icon: 'mdi-delete-alert-outline',
          title: this.$t('jobLogConfirmDeleteTitle'),
          body: this.$t('jobLogConfirmDeleteBody', { name: c.name, id: c.id }),
          note: this.$t('jobLogConfirmDeleteNote'),
          button: this.$t('jobLogActionDelete'),
          buttonIcon: 'mdi-delete',
        };
      }
      if (c.kind === 'trigger') {
        return {
          color: 'primary',
          icon: 'mdi-play-circle-outline',
          title: this.$t('jobLogConfirmTriggerTitle'),
          body: this.$t('jobLogConfirmTriggerBody', { id: c.id }),
          note: this.$t('jobLogConfirmTriggerNote'),
          button: this.$t('jobLogActionTrigger'),
          buttonIcon: 'mdi-play',
        };
      }
      const scheduled = c.state === 'Scheduled';
      return {
        color: 'primary',
        icon: scheduled ? 'mdi-play-speed' : 'mdi-replay',
        title: scheduled ? this.$t('jobLogConfirmEnqueueNowTitle') : this.$t('jobLogConfirmRequeueTitle'),
        body: scheduled
          ? this.$t('jobLogConfirmEnqueueNowBody', { name: c.name, id: c.id })
          : this.$t('jobLogConfirmRequeueBody', { name: c.name, id: c.id }),
        note: this.$t('jobLogConfirmRequeueNote'),
        button: this.requeueLabel(c.state),
        buttonIcon: scheduled ? 'mdi-play-speed' : 'mdi-replay',
      };
    },
  },

  watch: {
    // "หยุดอัตโนมัติ" ต้องหยุดยิงจริง ไม่ใช่ยิงแล้วทิ้งผล
    autoRefresh(on) {
      if (on) this.startPoll();
      else this.stopPoll();
    },
    /**
     * สลับแท็บ — ล้างตารางแล้วกลับไปหน้าแรก · ตาราง state mount ใหม่ตาม :key แล้วยิง
     * update:options เอง (⚠️ ไม่เรียก loadJobs ตรงนี้ ไม่งั้นโหลดซ้ำสองรอบ)
     */
    activeTab() {
      this.jobsSeq += 1;
      this.page = 1;
      this.rows = [];
      this.total = 0;
      this.jobsError = null;
      this.jobsStale = null;
      this.loadingJobs = false;
    },
    searchInput(value) {
      if (this.searchTimer) clearTimeout(this.searchTimer);
      this.searchTimer = setTimeout(() => {
        this.searchTimer = null;
        const q = (value || '').trim();
        if (q === this.search) return;
        this.search = q;
        if (!this.isRecurringTab) this.reloadFromFirstPage();
      }, SEARCH_DEBOUNCE_MS);
    },
    hideEveryMinute() {
      if (!this.isRecurringTab) this.reloadFromFirstPage();
    },
  },

  mounted() {
    this.loadOverview(false);
    this.startClock();
    this.startPoll();
    // ⚠️ ไม่เรียก loadJobs() ตรงนี้ — v-data-table-server ยิง update:options ตอน mount เอง
  },

  /** ⚠️ ต้องหยุดตัวจับเวลาทุกตัว ไม่งั้นมันไล่ถาม API ต่อทั้งที่ไม่มีใครดู และซ้อนกันเมื่อเข้า-ออกหน้านี้หลายรอบ */
  beforeUnmount() {
    this.stopPoll();
    this.stopClock();
    if (this.searchTimer) clearTimeout(this.searchTimer);
  },

  methods: {
    // ══ โหลดข้อมูล ══════════════════════════════════════════════════════════════
    async loadOverview(quiet = true) {
      if (quiet !== true) this.loadingOverview = true;
      try {
        this.overview = await jobLogApi.getOverview();
        this.lastUpdatedAt = Date.now();
        this.pageError = null;
        this.overviewStale = null;
      } catch (err) {
        const friendly = this.friendlyError(err);
        // ⚠️ มีของเดิมอยู่แล้วห้ามล้างจอ — ขึ้นป้ายว่าเป็นของเก่าแทน
        if (this.overview) this.overviewStale = friendly;
        else this.pageError = friendly;
        // ไม่มีสิทธิ์ / API ไม่มีฟีเจอร์นี้ — ถามซ้ำทุก 5 วินาทีก็ได้คำตอบเดิม หยุดไว้ก่อน
        if (friendly.persistent) this.autoRefresh = false;
      } finally {
        this.loadingOverview = false;
      }
    },

    async loadJobs(quiet = false) {
      if (this.isRecurringTab) return;
      this.jobsSeq += 1;
      const seq = this.jobsSeq;
      if (quiet !== true) this.loadingJobs = true;
      try {
        const data = await jobLogApi.getJobs({
          state: this.activeTab,
          from: (this.page - 1) * this.pageSize,
          count: this.pageSize,
          search: this.search,
          hideEveryMinute: this.hideEveryMinute,
        });
        if (seq !== this.jobsSeq) return;
        this.rows = (data && data.items) || [];
        this.total = Number(data && data.total) || 0;
        this.jobsError = null;
        this.jobsStale = null;
        // งานหายไประหว่างดู (หมดอายุ/เปลี่ยน state) จนหน้าปัจจุบันเกินหน้าสุดท้าย — ถอยกลับมา
        const lastPage = Math.max(1, Math.ceil(this.total / this.pageSize));
        if (this.page > lastPage) this.page = lastPage;
      } catch (err) {
        if (seq !== this.jobsSeq) return;
        const friendly = this.friendlyError(err);
        if (this.rows.length) this.jobsStale = friendly;
        else this.jobsError = friendly;
      } finally {
        if (seq === this.jobsSeq) this.loadingJobs = false;
      }
    },

    /** รอบ poll / ปุ่มรีเฟรช — overview + แท็บปัจจุบัน (ไม่แตะ dialog รายละเอียด) */
    async refresh() {
      // กันคำขอซ้อนกัน — รอบ poll กับปุ่มรีเฟรชชนกันได้
      if (this.inFlight) return;
      this.inFlight = true;
      try {
        const tasks = [this.loadOverview(true)];
        if (!this.isRecurringTab) tasks.push(this.loadJobs(true));
        await Promise.all(tasks);
      } finally {
        this.inFlight = false;
      }
    },

    /** เปลี่ยนตัวกรอง — กลับหน้าแรก · ถ้าหน้าไม่ได้อยู่ที่ 1 ตารางจะยิง update:options ให้เอง */
    reloadFromFirstPage() {
      if (this.page !== 1) this.page = 1;
      else this.loadJobs();
    },

    onTableOptions({ page, itemsPerPage }) {
      this.page = page;
      this.pageSize = itemsPerPage;
      this.loadJobs();
    },

    onRowClick(_event, { item }) {
      if (item && item.jobId) this.openDetail(item.jobId);
    },

    /**
     * แปลง error เป็นข้อความที่บอกว่าต้องไปแก้ที่ไหน
     * @param notFoundKey คีย์ข้อความเมื่อ server ตอบ 404 + APIStatus.NotFound (เช่น งานหมดอายุไปแล้ว)
     */
    friendlyError(err, notFoundKey) {
      const code = err && err.httpStatus;
      if (code === 401 || code === 403) {
        return { message: this.$t('jobLogForbidden'), persistent: true, icon: 'mdi-lock-outline' };
      }
      if (err && err.isRouteMissing) {
        return { message: this.$t('jobLogRouteMissing'), persistent: true, icon: 'mdi-api-off' };
      }
      if (code === 404) {
        if (err.status === API_STATUS_NOT_FOUND) {
          if (notFoundKey) return { message: this.$t(notFoundKey), persistent: false, icon: 'mdi-help-circle-outline' };
        } else {
          // 404 ที่ไม่ใช่ ResponseModel = ไม่มี endpoint นี้บน API ที่ deploy อยู่
          return { message: this.$t('jobLogRouteMissing'), persistent: true, icon: 'mdi-api-off' };
        }
      }
      return {
        message: (err && err.message) || this.$t('jobLogUnknownError'),
        persistent: false,
        icon: 'mdi-alert-circle-outline',
      };
    },

    // ══ polling ════════════════════════════════════════════════════════════════
    startPoll() {
      if (this.poll) return;
      this.poll = setInterval(() => {
        // แท็บ browser ที่ถูกซ่อนไม่ต้องถาม — กลับมาเปิดแล้วได้ค่าใหม่ในไม่กี่วินาทีเอง
        if (typeof document !== 'undefined' && document.hidden) return;
        this.refresh();
      }, REFRESH_MS);
    },
    stopPoll() {
      if (!this.poll) return;
      clearInterval(this.poll);
      this.poll = null;
    },
    startClock() {
      if (this.clockTimer) return;
      this.clockTimer = setInterval(() => {
        this.now = Date.now();
      }, CLOCK_MS);
    },
    stopClock() {
      if (!this.clockTimer) return;
      clearInterval(this.clockTimer);
      this.clockTimer = null;
    },

    // ══ รายละเอียด ═════════════════════════════════════════════════════════════
    async openDetail(jobId) {
      if (!jobId) return;
      const id = String(jobId);
      this.detailSeq += 1;
      const seq = this.detailSeq;
      this.detailOpen = true;
      this.detailLoading = true;
      this.detailError = null;
      // เปิดงานเดิมซ้ำ (หลังสั่งงาน) คงของเดิมไว้ระหว่างโหลด ไม่ให้ dialog กระพริบว่าง
      if (!this.detail || String(this.detail.jobId) !== id) this.detail = null;
      try {
        const data = await jobLogApi.getJob(id);
        if (seq !== this.detailSeq) return;
        this.detail = data;
      } catch (err) {
        if (seq !== this.detailSeq) return;
        this.detailError = this.friendlyError(err, 'jobLogJobNotFound');
      } finally {
        if (seq === this.detailSeq) this.detailLoading = false;
      }
    },

    dataEntries(entry) {
      const data = (entry && entry.data) || {};
      return Object.keys(data).map((key) => {
        const raw = data[key] === null || data[key] === undefined ? '' : String(data[key]);
        const long = raw.length > 120 || raw.includes('\n');
        return { key, value: long ? raw : this.dataValue(key, raw), long };
      });
    },

    /** ค่าใน data ของ state — เวลา (…At) แปลงเป็นเวลาไทย, ms (PerformanceDuration/Latency) แปลงเป็นข้อความ */
    dataValue(key, raw) {
      if (/At$/.test(key)) {
        const date = parseUtcDate(raw);
        if (date) return this.stamp(date);
      }
      if (key === 'PerformanceDuration' || key === 'Latency') {
        const ms = Number(raw);
        if (raw !== '' && Number.isFinite(ms)) return `${this.fmtDuration(ms)} (${raw} ms)`;
      }
      return raw || '—';
    },

    // ══ สั่งงาน ════════════════════════════════════════════════════════════════
    canRequeue(state) {
      return REQUEUE_STATES.includes(state);
    },
    canDelete(state) {
      return DELETE_STATES.includes(state);
    },
    requeueLabel(state) {
      return state === 'Scheduled' ? this.$t('jobLogActionEnqueueNow') : this.$t('jobLogActionRequeue');
    },
    askRequeue(job) {
      this.confirm = { kind: 'requeue', id: String(job.jobId), name: job.name || job.method || job.jobId, state: job.state };
      this.confirmOpen = true;
    },
    askDelete(job) {
      this.confirm = { kind: 'delete', id: String(job.jobId), name: job.name || job.method || job.jobId, state: job.state };
      this.confirmOpen = true;
    },
    askTrigger(row) {
      this.confirm = { kind: 'trigger', id: String(row.id), name: row.jobName || row.id, state: null };
      this.confirmOpen = true;
    },
    async runConfirmed() {
      const c = this.confirm;
      if (!c || this.actionBusy) return;
      this.actionBusy = true;
      try {
        if (c.kind === 'requeue') await jobLogApi.requeueJob(c.id);
        else if (c.kind === 'delete') await jobLogApi.deleteJob(c.id);
        else await jobLogApi.triggerRecurring(c.id);

        this.confirmOpen = false;
        this.notify(this.$t(DONE_KEYS[c.kind], { name: c.name, id: c.id }), 'success');

        // dialog รายละเอียดของงานเดียวกันเปิดอยู่ — อ่านใหม่ให้ state ตรงกับที่เพิ่งสั่ง
        if (c.kind !== 'trigger' && this.detailOpen && this.detail && String(this.detail.jobId) === c.id) {
          this.openDetail(c.id);
        }
        // ไม่ผ่าน refresh() เพราะ inFlight ของรอบ poll จะทำให้ข้ามไปแล้วจอค้างของเก่าอีก 5 วินาที
        this.loadOverview(true);
        if (!this.isRecurringTab) this.loadJobs(true);
      } catch (err) {
        this.confirmOpen = false;
        this.notify(this.friendlyError(err).message, 'error');
      } finally {
        this.actionBusy = false;
      }
    },
    notify(text, color) {
      this.snackbar = { show: true, text, color };
    },

    // ══ ตัวช่วยแสดงผล ══════════════════════════════════════════════════════════
    stateLabel(state) {
      const meta = STATE_META[state];
      return meta ? this.$t(meta.labelKey) : state || '—';
    },
    stateColor(state) {
      const meta = STATE_META[state];
      return meta ? meta.color : undefined;
    },
    stateIcon(state) {
      const meta = STATE_META[state];
      return meta ? meta.icon : 'mdi-circle-outline';
    },
    isEveryMinuteJob(item) {
      return Boolean(item && item.recurringJobId) && this.everyMinuteIds.includes(item.recurringJobId);
    },
    isLockTimeout(exceptionType) {
      if (!exceptionType) return false;
      return LOCK_TIMEOUT_TYPES.some((t) => String(exceptionType).includes(t));
    },
    isHeartbeatStale(value) {
      const t = this.ts(value);
      return t !== null && this.now - t > HEARTBEAT_STALE_MS;
    },
    /** ชื่อ exception แบบสั้น (ตัด namespace) — ชื่อเต็มอยู่ใน dialog รายละเอียด */
    shortType(type) {
      if (!type) return '';
      return String(type).split('.').pop();
    },
    argsText(args) {
      if (!Array.isArray(args) || !args.length) return '—';
      return args.join(', ');
    },
    truncate(text, max) {
      const s = text === null || text === undefined ? '' : String(text);
      return s.length > max ? `${s.slice(0, max)}…` : s;
    },
    num(value) {
      const n = Number(value);
      return Number.isFinite(n) ? n.toLocaleString('en-US') : '0';
    },

    // ── เวลา (ทุกตัวผ่าน utils/thaiTime.js — ปักที่ Asia/Bangkok) ──
    ts(value) {
      const date = parseUtcDate(value);
      return date ? date.getTime() : null;
    },
    /** DD/MM/YYYY เวลาไทย */
    day(value) {
      const s = fmtThaiDateTime(value, '');
      return s ? s.slice(0, 10) : '';
    },
    /** HH:mm:ss เวลาไทย */
    clock(value) {
      return fmtThaiTime(value, '—');
    },
    /** DD/MM/YYYY HH:mm:ss เวลาไทย */
    stamp(value) {
      const d = this.day(value);
      return d ? `${d} ${this.clock(value)}` : '—';
    },
    stampRelative(value) {
      const s = this.stamp(value);
      const r = this.relative(value);
      return r && s !== '—' ? `${s} (${r})` : s;
    },
    /**
     * "5 นาทีที่แล้ว" / "อีก 3 นาที" — รองรับอนาคตด้วย (รอบถัดไปของ recurring / งานที่ตั้งเวลา)
     * ⚠️ ไม่ใช้ fmtThaiRelative ของ thaiTime.js เพราะคืนภาษาไทยตายตัวและไม่รองรับเวลาในอนาคต
     */
    relative(value) {
      const t = this.ts(value);
      if (t === null) return '';
      const diff = this.now - t;
      // ต่างกันไม่กี่วินาที = นาฬิกาเครื่องกับ server เหลื่อมกัน ไม่ใช่อนาคต/อดีตจริง
      if (Math.abs(diff) < 5000) return this.$t('jobLogJustNow');
      const span = this.spanText(Math.abs(diff));
      return diff > 0 ? this.$t('jobLogAgo', { d: span }) : this.$t('jobLogIn', { d: span });
    },
    spanText(ms) {
      const sec = Math.floor(ms / 1000);
      if (sec < 60) return this.$t('jobLogDurSeconds', { n: sec });
      const min = Math.floor(sec / 60);
      if (min < 60) return this.$t('jobLogDurMinutes', { n: min });
      const hr = Math.floor(min / 60);
      if (hr < 24) return this.$t('jobLogDurHours', { n: hr });
      const days = Math.floor(hr / 24);
      // ⚠️ อังกฤษต้องแยกเอกพจน์/พหูพจน์เอง — "1 days ago" อ่านแล้วสะดุด
      return this.$t(days === 1 ? 'jobLogDurDay' : 'jobLogDurDays', { n: days });
    },
    /** ระยะเวลาที่รันมาแล้ว (นับสดตามนาฬิกาหน้าจอ) */
    elapsedSince(value) {
      const t = this.ts(value);
      if (t === null) return '—';
      return this.fmtElapsed(Math.max(0, this.now - t));
    },
    fmtElapsed(ms) {
      const sec = Math.floor(ms / 1000);
      if (sec < 60) return this.$t('jobLogDurSeconds', { n: sec });
      const min = Math.floor(sec / 60);
      if (min < 60) return this.$t('jobLogDurMinSec', { m: min, s: sec % 60 });
      return this.$t('jobLogDurHourMin', { h: Math.floor(min / 60), m: min % 60 });
    },
    /** ms → ข้อความ (durationMs ของงานที่สำเร็จ มักต่ำกว่าวินาที) */
    fmtDuration(ms) {
      if (ms === null || ms === undefined || ms === '') return '—';
      const n = Number(ms);
      if (!Number.isFinite(n)) return '—';
      if (n < 1000) return this.$t('jobLogDurMs', { n: Math.round(n) });
      if (n < 60000) return this.$t('jobLogDurSeconds', { n: (n / 1000).toFixed(1) });
      return this.fmtElapsed(n);
    },
  },
};
</script>

<style scoped>
.title {
  font-size: 18px;
  font-weight: 500;
}

.status-alert {
  border-radius: 12px;
}

.note-list {
  padding-left: 18px;
  margin: 0;
}

.note-list li {
  margin-bottom: 2px;
}

.kpi-card {
  transition: border-color 0.15s ease;
}

.kpi-card--active {
  border-color: rgb(var(--v-theme-primary));
}

.search-field {
  flex: 1 1 280px;
  max-width: 420px;
}

.mono {
  font-family: ui-monospace, SFMono-Regular, Menlo, monospace;
}

/* ชื่องานต้องไม่ดันคอลัมน์อื่น — ตัดด้วย ellipsis */
.job-cell {
  min-width: 0;
  max-width: 420px;
}

.args-cell {
  word-break: break-all;
}

.server-cell {
  display: inline-block;
  max-width: 260px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  vertical-align: middle;
}

.exc-cell {
  max-width: 480px;
}

.clamp-2 {
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
  word-break: break-word;
}

/* ตารางตาม docs/Table-Style-Guide.md (ส่วนที่เหลืออยู่ใน assets/qa-console.css) */
:deep(.my-data-table .v-table__wrapper) {
  max-height: 560px !important;
  overflow: auto !important;
}

.action-buttons-group {
  display: flex;
  gap: 4px;
  justify-content: flex-end;
  align-items: center;
}

.action-btn {
  min-width: 36px !important;
  width: 36px;
  height: 36px;
  border-radius: 6px;
}

.servers-table :deep(.v-table__wrapper) {
  max-height: 260px !important;
}

.section-title {
  font-size: 14px;
  font-weight: 500;
  margin-bottom: 6px;
}

.code-block {
  background: rgba(var(--v-theme-on-surface), 0.04);
  border-radius: 6px;
  padding: 8px 10px;
  font-family: ui-monospace, SFMono-Regular, Menlo, monospace;
  font-size: 12px;
  line-height: 1.5;
  max-height: 240px;
  overflow: auto;
  white-space: pre-wrap;
  word-break: break-word;
}

.code-block--tall {
  max-height: 360px;
}

.kv-table {
  width: 100%;
  border-collapse: collapse;
  font-size: 12px;
}

.kv-table td {
  padding: 3px 8px;
  vertical-align: top;
  border-bottom: 1px solid rgba(var(--v-border-color), var(--v-border-opacity));
}

.kv-key {
  width: 200px;
  white-space: nowrap;
  opacity: 0.7;
}

.history-item {
  padding: 10px 0;
  border-bottom: 1px dashed rgba(var(--v-border-color), var(--v-border-opacity));
}

.history-item:last-child {
  border-bottom: none;
}

.text-break {
  word-break: break-word;
}
</style>
